"use client"

import { useState, useEffect } from "react"
import { Save, Eye, EyeOff, Globe, ArrowLeft, BookOpen } from "lucide-react"
import "./styles/AdminApp.css"
import LessonPreview from "./components/LessonPreview"
import EditorPanel from "./components/EditorPanel"
import { PublishManager } from "./components/PublishManager"

// Language options
const LANGUAGES = [
  { code: "nl", name: "Dutch" },
  { code: "en", name: "English" },
]

export interface QuizOption {
  text: string
  correct: boolean
  feedback: string
}

export interface ListItem {
  text: string
  highlightAtSrtIndex?: number
}

export interface StepContent {
  title: string
  description: string
  audioSrc?: string
  subtitleSrc?: string
  srtSrc?: string
  spokenContent?: string
  videoUrl?: string
  videoSrc?: string
  fullscreenContent?: boolean
  fullHeightVideo?: boolean
  hideAudioControls?: boolean
  visualizationType?: string
  chartData?: any
  options?: QuizOption[]
  image?: string
  columns?: Array<{
    textColor?: string
    titleAlignment?: string
    listItems?: ListItem[]
    imageSrc?: string
    imageAlt?: string
    title?: string
    description?: string
    imageChanges?: Array<{
      srtIndex: number
      src: string
    }>
  }>
  tools?: Array<{
    observations?: Array<{
      text: string
    }>
  }>
  tips?: Array<{
    icon?: string
    text: string
  }>
  links?: Array<{ text: string; url: string }>
}

export interface Step {
  type: string
  content: StepContent
  locked?: boolean
}

export interface Section {
  key: string
  title: string
  emoji: string
  steps: Step[]
  achievements?: any[]
  config?: {
    avatar?: string
    avatarAlignment?: string
    avatarPosition?: any
    avatarScale?: number
    mobilePosition?: any
    mobileScale?: number
    right?: string
    bottom?: string
    left?: string
    top?: string
    mobileRight?: string
    mobileBottom?: string
  }
}

export interface GameConfig {
  title: string
  description: string
  emoji: string
  category: string
  difficulty: string
  ageRange: string
  iconImage?: string
  comingSoon?: boolean
  locked?: boolean
  avatar?: string
  avatarAlignment?: string
  avatarPosition?: any
  avatarScale?: number
  mobilePosition?: any
  mobileScale?: number
  right?: string
  bottom?: string
  left?: string
  top?: string
  mobileRight?: string
  mobileBottom?: string
}

function AdminApp() {
  const [selectedLanguage, setSelectedLanguage] = useState("nl")
  const [lessonId, setLessonId] = useState("")
  const [gameConfig, setGameConfig] = useState<GameConfig>({
    title: "",
    description: "",
    emoji: "📱",
    category: "",
    difficulty: "Beginner",
    ageRange: "8-12",
  })
  const [sections, setSections] = useState<Section[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [selectedStep, setSelectedStep] = useState<{ sectionIndex: number; stepIndex: number } | null>(null)
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [showPreview, setShowPreview] = useState(true)
  const [showLessonBrowser, setShowLessonBrowser] = useState(false)
  const [showNewLessonModal, setShowNewLessonModal] = useState(false)
  const [showPublishManager, setShowPublishManager] = useState(false)
  const [newLessonId, setNewLessonId] = useState("")
  const [existingLessons, setExistingLessons] = useState<string[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  // Show message helper
  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  // Save lesson
  const saveLesson = async () => {
    if (!lessonId) {
      showMessage("error", "Please enter a lesson ID")
      return
    }

    if (sections.length === 0) {
      showMessage("error", "Cannot save lesson with no sections")
      return
    }

    setIsSaving(true)

    try {
      console.log("[v0] Saving lesson:", lessonId)
      console.log("[v0] Number of sections:", sections.length)
      console.log(
        "[v0] Section titles:",
        sections.map((s) => s.title),
      )

      const lessonData = {
        gameConfig,
        sections: sections.map((_, index) => `section-${index + 1}.json`),
      }

      console.log("[v0] Index data:", lessonData)

      const response = await fetch("/portal/lesson-admin-api.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          lessonId,
          language: selectedLanguage,
          indexData: lessonData,
          sectionsData: sections,
        }),
      })

      console.log("[v0] Save response status:", response.status)
      const result = await response.json()
      console.log("[v0] Save response data:", result)

      if (!response.ok) throw new Error(result.error || "Failed to save lesson")
      showMessage("success", "Lesson saved successfully!")

      await loadLesson()
    } catch (error: any) {
      console.error("[v0] Save error:", error)
      showMessage("error", error.message || "Failed to save lesson")
    } finally {
      setIsSaving(false)
    }
  }

  // Load lesson
  const loadLesson = async () => {
    if (!lessonId) return

    console.log("[v0] Loading lesson:", lessonId, "language:", selectedLanguage)

    try {
      const response = await fetch(
        `/portal/lesson-admin-api.php?action=load&lessonId=${lessonId}&language=${selectedLanguage}`,
      )

      console.log("[v0] Load response status:", response.status)

      const data = await response.json()
      console.log("[v0] API response:", data)

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load lesson")
      }

      setGameConfig(data.gameConfig)
      setSections(data.sections)
      setIsLoaded(true)
      showMessage("success", `Loaded: ${lessonId}`)
    } catch (error: any) {
      console.error("[v0] Failed to load lesson:", error)
      setIsLoaded(false)
      if (lessonId && existingLessons.includes(lessonId)) {
        showMessage("error", error.message || "Failed to load lesson")
      }
    }
  }

  const startNewLesson = () => {
    setShowNewLessonModal(true)
    setNewLessonId("")
  }

  const createNewLesson = () => {
    if (!newLessonId.trim()) {
      showMessage("error", "Please enter a lesson ID")
      return
    }

    if (existingLessons.includes(newLessonId)) {
      showMessage("error", "Lesson ID already exists. Choose a different ID.")
      return
    }

    setLessonId(newLessonId)
    setGameConfig({
      title: "",
      description: "",
      emoji: "📱",
      category: "",
      difficulty: "Beginner",
      ageRange: "8-12",
      iconImage: "",
      comingSoon: false,
      locked: false,
    })
    setSections([
      {
        key: "section-1",
        title: "Introduction",
        emoji: "👋",
        steps: [
          {
            type: "intro",
            content: {
              title: "Welcome!",
              description: "Start editing this step",
              image: "",
              columns: [],
              links: [],
            },
          },
        ],
      },
    ])
    setIsLoaded(true)
    setShowNewLessonModal(false)
    showMessage("success", `Created new lesson: ${newLessonId}`)
  }

  const selectLesson = (id: string) => {
    setLessonId(id)
    setShowLessonBrowser(false)
  }

  const fetchExistingLessons = async () => {
    console.log("[v0] Fetching existing lessons for language:", selectedLanguage)

    try {
      const response = await fetch(`/portal/lesson-admin-api.php?action=list&language=${selectedLanguage}`)
      console.log("[v0] List response status:", response.status)

      const data = await response.json()
      console.log("[v0] API response:", data)

      if (response.ok && data.success) {
        setExistingLessons(data.lessons || [])
      } else {
        console.error("[v0] API error:", data.error)
        showMessage("error", data.error || "Failed to fetch lessons")
      }
    } catch (error) {
      console.error("[v0] Failed to fetch lessons:", error)
      showMessage("error", "Failed to connect to API")
    }
  }

  useEffect(() => {
    console.log("[v0] App mounted successfully")
    console.log("[v0] Current lessonId:", lessonId)
    console.log("[v0] Current language:", selectedLanguage)
  }, [])

  useEffect(() => {
    if (lessonId) {
      loadLesson()
    } else {
      setIsLoaded(false)
    }
  }, [lessonId, selectedLanguage])

  useEffect(() => {
    if (showLessonBrowser) {
      fetchExistingLessons()
    }
  }, [showLessonBrowser, selectedLanguage])

  console.log("[v0] Rendering App component")

  return (
    <div className="visual-editor">
      {/* Message Toast */}
      {message && <div className={`toast toast-${message.type}`}>{message.text}</div>}

      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          <a
            href="/portal/admin.php"
            className="btn-sm btn-secondary"
            style={{
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              marginRight: "12px",
            }}
          >
            <ArrowLeft size={16} />
            Admin
          </a>
          <h1 style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <BookOpen size={20} color="#5F29FF" />
            Techtok Lesson Editor
          </h1>
          {isLoaded && <span className="loaded-badge">Loaded: {lessonId}</span>}
        </div>
        <div className="top-bar-controls">
          <select className="select-sm" value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
          <button className="btn-sm btn-secondary" onClick={() => setShowLessonBrowser(!showLessonBrowser)}>
            Browse
          </button>
          <input
            className="input-sm"
            placeholder="Enter lesson ID to load..."
            value={lessonId}
            onChange={(e) => setLessonId(e.target.value)}
          />
          <button className="btn-sm btn-secondary" onClick={startNewLesson}>
            New
          </button>
          <button className="btn-sm btn-secondary" onClick={() => setShowPublishManager(true)}>
            <Globe size={16} />
            Publish
          </button>
          <button className="btn-sm btn-secondary" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <button onClick={saveLesson} disabled={isSaving} className="btn-sm btn-primary">
            <Save size={16} />
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* New Lesson Modal */}
      {showNewLessonModal && (
        <div className="modal-overlay" onClick={() => setShowNewLessonModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Lesson</h2>
              <button className="modal-close" onClick={() => setShowNewLessonModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Enter a unique ID for your new lesson (e.g., "ai-makers", "app-designer")
              </p>
              <input
                className="input-full"
                placeholder="Lesson ID (lowercase, use hyphens)"
                value={newLessonId}
                onChange={(e) => setNewLessonId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    createNewLesson()
                  }
                }}
                autoFocus
              />
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowNewLessonModal(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={createNewLesson}>
                  Create Lesson
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLessonBrowser && (
        <div className="modal-overlay" onClick={() => setShowLessonBrowser(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Browse Existing Lessons</h2>
              <button className="modal-close" onClick={() => setShowLessonBrowser(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {existingLessons.length === 0 ? (
                <p className="no-lessons">No lessons found for {selectedLanguage}</p>
              ) : (
                <div className="lesson-list">
                  {existingLessons.map((id) => (
                    <button key={id} className="lesson-item" onClick={() => selectLesson(id)}>
                      <span className="lesson-id">{id}</span>
                      <span className="lesson-arrow">→</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PublishManager Modal */}
      {showPublishManager && (
        <PublishManager selectedLanguage={selectedLanguage} onClose={() => setShowPublishManager(false)} />
      )}

      {/* Split Screen Layout */}
      <div className="split-layout">
        {/* Left: Live Preview */}
        {showPreview && (
          <div className="preview-pane">
            <LessonPreview
              gameConfig={gameConfig}
              sections={sections}
              currentSectionIndex={currentSectionIndex}
              currentStepIndex={currentStepIndex}
              onStepClick={(sectionIndex, stepIndex) => {
                setSelectedStep({ sectionIndex, stepIndex })
                setCurrentSectionIndex(sectionIndex)
                setCurrentStepIndex(stepIndex)
              }}
            />
          </div>
        )}

        {/* Right: Editor Panel */}
        <div className={`editor-pane ${!showPreview ? "editor-pane-full" : ""}`}>
          <EditorPanel
            gameConfig={gameConfig}
            setGameConfig={setGameConfig}
            sections={sections}
            setSections={setSections}
            selectedStep={selectedStep}
            setSelectedStep={setSelectedStep}
            currentSectionIndex={currentSectionIndex}
            setCurrentSectionIndex={setCurrentSectionIndex}
            currentStepIndex={currentStepIndex}
            setCurrentStepIndex={setCurrentStepIndex}
            lessonId={lessonId}
            language={selectedLanguage}
          />
        </div>
      </div>
    </div>
  )
}

export default AdminApp
